import { cloneElement, useId, type ReactElement, type ReactNode } from 'react';
import { SimpleTooltip } from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';

// A disabled <button> takes no focus or hover, so the reason sits on a
// focusable wrapper: tooltip for pointer/keyboard, description for screen readers.
export function DisabledReason({
  reason,
  children,
  className,
}: {
  reason: string | null;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  if (!reason) return children;
  return (
    <SimpleTooltip content={reason}>
      <span
        tabIndex={0}
        aria-describedby={id}
        className={cn(
          'inline-flex shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        {children}
        <span id={id} className="sr-only">
          {reason}
        </span>
      </span>
    </SimpleTooltip>
  );
}

// For a control that stays focusable: marks it aria-disabled and describes why.
export function AriaDisabledReason({ reason, children }: { reason: string | null; children: ReactElement }) {
  const id = useId();
  if (!reason) return children;
  return (
    <>
      <SimpleTooltip content={reason}>
        {cloneElement(children as ReactElement<Record<string, unknown>>, {
          'aria-disabled': true,
          'aria-describedby': id,
        })}
      </SimpleTooltip>
      <span id={id} className="sr-only">
        {reason}
      </span>
    </>
  );
}
